import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useSession } from '../../lib/auth/session.js';
import { api } from '../../lib/api/client.js';
import { Empty, ErrorBanner, Segmented, TableSkeleton } from '../../components/ui/Primitives.js';
import { Icon } from '../../components/ui/Icon.js';
import type { AgentAttendance, AgentPauseReason } from '@arcnet/contracts';

/**
 * Attendance: who was logged on, for how long, and what the time went on (API agentshifts.ts).
 *
 * The question a supervisor actually asks is "was the day worked", so the row leads with hours
 * logged on and the share of them that counted as work, and the pauses are broken out beside it
 * rather than hidden in a total. A pause the account calls work -- training, coaching -- counts
 * as work here, because that is the account's decision to make and it is made once, on the
 * reason, rather than every time somebody reads a report.
 */

const DAY = 86_400_000;

/** SAST, like everything else people read here: a day runs midnight to midnight where they are. */
const OFFSET = '+02:00';
const dayStart = (d: Date): string =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}T00:00:00${OFFSET}`;

type Period = 'today' | 'yesterday' | 'week' | 'month';

const PERIODS: ReadonlyArray<readonly [Period, string]> = [
  ['today', 'Today'], ['yesterday', 'Yesterday'], ['week', 'Last 7 days'], ['month', 'Last 30 days'],
];

function range(period: Period): { from: string; to: string } {
  const now = new Date();
  const today = dayStart(now);
  if (period === 'today') return { from: today, to: dayStart(new Date(now.getTime() + DAY)) };
  if (period === 'yesterday') {
    return { from: dayStart(new Date(now.getTime() - DAY)), to: today };
  }
  const days = period === 'week' ? 7 : 30;
  return { from: dayStart(new Date(now.getTime() - days * DAY)), to: dayStart(new Date(now.getTime() + DAY)) };
}

/** Hours and minutes, never seconds: nobody reads a shift to the second. */
const hm = (sec: number): string => {
  const h = Math.floor(sec / 3600);
  const m = Math.round((sec % 3600) / 60);
  return h > 0 ? `${h}h ${String(m).padStart(2, '0')}m` : `${m}m`;
};

const clock = (iso: string | null): string => (iso
  ? new Date(iso).toLocaleTimeString('en-ZA', { hour: '2-digit', minute: '2-digit', hour12: false })
  : '—');

export function AttendancePage() {
  const tenantId = useSession((s) => s.activeTenantId);
  const [period, setPeriod] = useState<Period>('today');
  const { from, to } = useMemo(() => range(period), [period]);

  const q = useQuery({
    queryKey: ['agent-attendance', tenantId ?? '', from, to],
    queryFn: () => api.get<{ data: AgentAttendance[]; reasons: AgentPauseReason[] }>(
      `/v1/tenants/${tenantId}/agent-attendance?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}`),
    enabled: !!tenantId,
    refetchInterval: period === 'today' ? 60_000 : false,
  });

  if (!tenantId) {
    return <Empty icon="accounts" title="No account selected" hint="Choose an account to see its attendance." />;
  }

  const rows = q.data?.data ?? [];

  return (
    <>
      <div className="arc-page-head">
        <div>
          <h1 className="arc-h1">Attendance</h1>
          <p>Who was logged on, and what the time went on</p>
        </div>
      </div>

      {q.error && <ErrorBanner error={q.error} />}

      <div className="arc-card">
        <div className="arc-toolbar">
          <Segmented label="Period" value={period} onChange={setPeriod}
            options={PERIODS.map(([k, label]) => [k, label] as const)} />
          <div className="arc-spacer" />
          {rows.length > 0 && (
            <span className="arc-subtle arc-num" style={{ fontSize: 12.5 }}>
              {rows.length} {rows.length === 1 ? 'person' : 'people'}
            </span>
          )}
        </div>

        <div className="arc-table-wrap">
          <table className="arc-table">
            <colgroup>
              <col />
              <col style={{ width: 96 }} />
              <col style={{ width: 96 }} />
              <col style={{ width: 110 }} />
              <col style={{ width: 96 }} />
              <col style={{ width: '22%' }} />
              <col style={{ width: 120 }} />
            </colgroup>
            <thead>
              <tr>
                <th>Extension</th>
                <th>On shift</th>
                <th>On calls</th>
                <th>Productive</th>
                <th>Paused</th>
                <th>Pauses</th>
                <th>First / last</th>
              </tr>
            </thead>
            {q.data === undefined ? <TableSkeleton cols={7} /> : (
              <tbody>
                {rows.map((a) => (
                  <tr key={a.extension_id}>
                    <td>
                      <div className="arc-row" style={{ gap: 10 }}>
                        <span className="arc-ext-num">{a.extension_number}</span>
                        <div className="arc-stack" style={{ gap: 1 }}>
                          <span style={{ fontWeight: 550 }}>{a.display_name}</span>
                          <span className="arc-subtle" style={{ fontSize: 11.5 }}>
                            {a.shifts} {a.shifts === 1 ? 'shift' : 'shifts'}
                          </span>
                        </div>
                      </div>
                    </td>
                    <td className="arc-num">{hm(a.logged_on_sec)}</td>
                    <td className="arc-num arc-muted">{hm(a.on_call_sec)}</td>
                    <td>
                      <div className="arc-stack" style={{ gap: 2 }}>
                        <span className="arc-num" style={{ fontWeight: 550 }}>
                          {a.productive_pct === null ? '—' : `${a.productive_pct}%`}
                        </span>
                        {/* The bar is the same number said again, for scanning a column of
                            people rather than reading each one. */}
                        <span className="arc-meter" aria-hidden="true">
                          <span style={{ width: `${Math.min(100, a.productive_pct ?? 0)}%` }} />
                        </span>
                      </div>
                    </td>
                    <td className="arc-num arc-muted">{hm(a.paused_sec)}</td>
                    <td className="arc-muted">
                      {a.by_reason.length === 0 ? <span className="arc-subtle">—</span> : (
                        <div className="arc-row" style={{ gap: 6, flexWrap: 'wrap' }}>
                          {a.by_reason.map((r) => (
                            <span key={r.reason} className="arc-pause-chip" data-productive={r.productive || undefined}
                              title={r.productive ? 'Counts as work' : 'Does not count as work'}>
                              {r.label} {hm(r.seconds)}
                            </span>
                          ))}
                        </div>
                      )}
                    </td>
                    <td className="arc-muted arc-num">
                      {/* Still on shift is a fact, not a missing value: an em dash where the
                          logout should be reads as data we lost. */}
                      {clock(a.first_login)} – {a.last_logout ? clock(a.last_logout)
                        : <span className="arc-subtle" style={{ fontSize: 11.5 }}>on shift</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            )}
          </table>
        </div>

        {q.data !== undefined && rows.length === 0 && (
          <Empty icon="clock" title="Nobody was logged on"
            hint="Attendance starts when somebody logs on in the workspace." />
        )}
      </div>

      <p className="arc-hint" style={{ marginTop: 10 }}>
        <Icon name="info" size={12} /> Productive is time ready, on a call, wrapping up, and pauses
        this account counts as work. Which pauses those are is set on the reason itself.
      </p>
    </>
  );
}
